/**
 * 🎁 Розыгрыши: задания, прогресс, участие.
 *
 * Прогресс приходит с бэка при каждом запросе — подписка на канал это состояние, а не
 * событие. Поэтому после «Я подписался» мы не верим на слово, а перезапрашиваем список:
 * человек мог нажать кнопку, не открыв канал.
 *
 * Отдельно различаем «задание не выполнено» и «проверить не удалось» (`errored`):
 * при сбое Telegram бэк fail-closed, и показать «вы не подписаны» подписавшемуся
 * человеку — верный способ получить жалобу.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, Check, Copy, ExternalLink, Gift, Loader2, RefreshCw, Trophy, Users,
} from 'lucide-react';
import { api, type Giveaway, type GiveawayTaskState } from '@/lib/api';
import { copyToClipboard, telegramShareUrl } from '@/lib/clipboard';
import { IconChip, SectionLabel } from '@/components/ui/calm';
import {
  Skeleton,
  SkeletonCard,
  SkeletonLine,
  SkeletonPage,
  SkeletonParagraph,
} from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { haptic, openExternalUrl } from '@/lib/telegram';
import i18n from '@/i18n';

export const Route = createFileRoute('/giveaways')({ component: Giveaways });

function Giveaways() {
  const { t } = useTranslation();
  const { data, isPending } = useQuery({
    queryKey: ['giveaways'],
    queryFn: () => api.giveaways(),
  });

  if (isPending) return <GiveawaysSkeleton />;

  if (!data || data.length === 0) {
    return (
      <div className="p-4">
        <div className="rounded-[24px] p-8 text-center space-y-3 border border-white/[0.06] bg-white/[0.02]">
          <IconChip icon={Gift} tone="blue" size={52} className="mx-auto" />
          <h3 className="text-lg font-bold text-foreground">{t('No giveaways yet')}</h3>
          <p className="text-sm text-[#8a8c93] leading-snug">
            {t('When a giveaway starts, it will appear here.')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <SectionLabel>{t('Giveaways')}</SectionLabel>
      {data.map((g) => (
        <GiveawayCard key={g.id} giveaway={g} />
      ))}
    </div>
  );
}

function GiveawayCard({ giveaway: g }: { giveaway: Giveaway }) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const join = useMutation({
    mutationFn: () => api.joinGiveaway(g.id),
    onSuccess: () => {
      haptic('success');
      void qc.invalidateQueries({ queryKey: ['giveaways'] });
    },
    onError: () => {
      haptic('error');
      // Отдельного тоста не показываем: бэк уже вернул свежий прогресс, и
      // перезапрос покажет, какое именно задание не выполнено.
      void qc.invalidateQueries({ queryKey: ['giveaways'] });
    },
  });

  const recheck = useMutation({
    mutationFn: async () => qc.invalidateQueries({ queryKey: ['giveaways'] }),
    onSuccess: () => haptic('light'),
  });

  const drawing = g.status === 'drawing';
  const left = timeLeft(g.ends_at);

  return (
    <div
      className="rounded-[24px] p-6 relative overflow-hidden border border-primary/25 space-y-4"
      style={{
        background:
          'linear-gradient(168deg, rgb(var(--brand-primary-rgb) / 0.16), rgb(var(--brand-primary-rgb) / 0.04))',
      }}
    >
      <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-primary blur-[60px] opacity-25 pointer-events-none" />

      <div className="relative z-10 space-y-4">
        <div className="flex items-start gap-3">
          <IconChip icon={g.won ? Trophy : Gift} tone={g.won ? 'green' : 'blue'} size={42} />
          <div className="min-w-0 flex-1">
            <h4 className="text-lg font-bold text-foreground leading-tight">{g.title}</h4>
            <div className="text-[12px] text-[#8a8c93] mt-0.5">
              {t('Prize')}: <b className="text-foreground">{g.prize_name}</b>
              {g.winners_count > 1 && ` × ${g.winners_count}`}
            </div>
          </div>
        </div>

        {g.description && (
          <p className="text-sm text-[#c2c4ca] leading-snug whitespace-pre-wrap">
            {g.description}
          </p>
        )}

        <div className="flex items-center gap-4 text-[12px] text-[#8a8c93]">
          <span className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" />
            {g.entries_count}
          </span>
          <span>{drawing ? t('Drawing winners…') : left}</span>
        </div>

        {g.won ? (
          <div className="rounded-[15px] p-4 bg-[#38e0a5]/[0.12] border border-[#38e0a5]/25 text-sm text-foreground font-semibold text-center">
            <span className="inline-flex items-center justify-center gap-1.5">
              <Trophy className="w-4 h-4 shrink-0" aria-hidden />
              {t('You won! The prize is already on your account.')}
            </span>
          </div>
        ) : (
          <>
            <div className="space-y-2">
              {g.tasks.length === 0 ? (
                <div className="rounded-[15px] p-3.5 bg-white/[0.04] text-[13px] text-[#c2c4ca] text-center">
                  {t('Giveaway no tasks')}
                </div>
              ) : (
                g.tasks.map((task) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    referralLink={g.invite_link}
                  />
                ))
              )}
            </div>

            {g.joined ? (
              <div className="rounded-[15px] p-3.5 bg-white/[0.04] text-sm text-[#c2c4ca] text-center">
                {drawing
                  ? t('Drawing in progress — results will arrive in the bot.')
                  : t("You're in. We'll message you in the bot when it ends.")}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => join.mutate()}
                  disabled={!g.can_join || join.isPending || drawing}
                  className="flex-1 calm-cta font-bold py-3 rounded-xl text-sm active:scale-[0.98] transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {join.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Gift className="w-4 h-4" />
                  )}
                  {t('Take part')}
                </button>
                <button
                  type="button"
                  onClick={() => recheck.mutate()}
                  disabled={recheck.isPending}
                  aria-label={t('Check again')}
                  title={t('Check again')}
                  className="w-12 h-12 rounded-xl bg-white/[0.05] flex items-center justify-center active:scale-95 transition-transform disabled:opacity-50"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${recheck.isPending ? 'animate-spin' : ''}`}
                  />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function TaskRow({
  task,
  referralLink,
}: {
  task: GiveawayTaskState;
  referralLink: string | null;
}) {
  const { t } = useTranslation();
  const label =
    task.kind === 'channel'
      ? `${t('Subscribe to')} ${task.channel_name || t('the channel')}`
      : `${t('Invite friends who buy a plan')}`;

  const clickable = task.kind === 'channel' && !!task.channel_url && !task.done;
  // Ссылку показываем, пока задание не выполнено: после «3 из 3» она уже не нужна,
  // а место занимает.
  // Ссылка приходит вместе с розыгрышем, а не из профиля: при выключенной реферальной
  // программе профиль её не отдаёт, а участие засчитывается по покупке друга (тикет #33).
  const showLink = task.kind === 'invite' && !task.done && !!referralLink;

  const share = async () => {
    if (!referralLink) return;
    haptic('light');
    if (await copyToClipboard(referralLink)) {
      toast.success(t('Giveaway invite link copied'));
      return;
    }
    openExternalUrl(telegramShareUrl(referralLink, t('Giveaway share text')));
  };

  return (
    <div className="space-y-2">
    <button
      type="button"
      disabled={!clickable}
      onClick={() => {
        if (task.channel_url) openExternalUrl(task.channel_url);
      }}
      className={`w-full flex items-center gap-3 p-3 rounded-[15px] text-left ${
        clickable ? 'active:scale-[0.99] transition-transform' : ''
      }`}
      style={{ background: 'rgba(255,255,255,0.03)' }}
    >
      <div
        className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
          task.done
            ? 'bg-[#38e0a5]/20 text-[#5fe3ad]'
            : task.errored
              ? 'bg-amber-400/15 text-amber-300'
              : 'bg-white/[0.06] text-[#8a8c93]'
        }`}
      >
        {task.done ? (
          <Check className="w-4 h-4" strokeWidth={3} />
        ) : task.errored ? (
          <AlertTriangle className="w-3.5 h-3.5" />
        ) : (
          <span className="text-[11px] font-bold">
            {task.kind === 'invite' ? `${task.current}/${task.required}` : '—'}
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-foreground leading-snug">{label}</div>
        {task.kind === 'invite' && (
          <div className="text-[11px] text-[#8a8c93]">
            {task.current} {t('of')} {task.required}
          </div>
        )}
        {task.errored && (
          <div className="text-[11px] text-amber-300">
            {t('Could not check — try again in a minute.')}
          </div>
        )}
      </div>
      {clickable && <ExternalLink className="w-3.5 h-3.5 text-[#8a8c93] shrink-0" />}
    </button>

    {showLink && (
      <div className="rounded-[15px] p-3 space-y-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
        <div className="text-[11px] text-[#8a8c93]">{t('Giveaway invite link')}</div>
        <div className="text-[11.5px] text-foreground break-all font-mono leading-snug">
          {referralLink}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={share}
            className="flex-1 calm-cta py-2.5 rounded-xl text-[13px] font-bold active:scale-[0.98] transition-transform flex items-center justify-center gap-1.5"
          >
            <Copy className="w-3.5 h-3.5" />
            {t('Copy')}
          </button>
          <button
            type="button"
            onClick={() => {
              haptic('light');
              openExternalUrl(telegramShareUrl(referralLink!, t('Giveaway share text')));
            }}
            className="flex-1 bg-white/[0.06] py-2.5 rounded-xl text-[13px] font-bold active:scale-[0.98] transition-transform"
          >
            {t('Share')}
          </button>
        </div>
      </div>
    )}
    </div>
  );
}

/** «Осталось 2 дня» без внешних библиотек дат — их в этом фронте нет. */
function timeLeft(iso: string): string {
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return i18n.t('Giveaway soon');
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return i18n.t('Giveaway left min', { count: mins });
  const hours = Math.floor(mins / 60);
  if (hours < 48) return i18n.t('Giveaway left hour', { count: hours });
  return i18n.t('Giveaway left day', { count: Math.floor(hours / 24) });
}

/** Карточка розыгрыша: иконка приза, название с призом, описание, счётчики, кнопка. */
function GiveawaysSkeleton() {
  return (
    <SkeletonPage>
      {[0, 1].map((i) => (
        <SkeletonCard key={i} className="rounded-[24px] min-h-0 p-6 space-y-4">
          <div className="flex items-start gap-3">
            <Skeleton className="w-[42px] h-[42px] rounded-[14px] shrink-0" />
            <div className="min-w-0 flex-1 space-y-2">
              <SkeletonLine size="title" className="w-3/5" />
              <SkeletonLine size="sm" className="w-2/5" />
            </div>
          </div>
          <SkeletonParagraph lines={2} size="sm" />
          <div className="flex items-center gap-4">
            <SkeletonLine size="sm" className="w-12" />
            <SkeletonLine size="sm" className="w-24" />
          </div>
          <Skeleton className="h-12 w-full rounded-[15px]" />
        </SkeletonCard>
      ))}
    </SkeletonPage>
  );
}
